import { motion, useMotionValue, useTransform } from 'motion/react'
import { cn } from '#app/utils/misc'
import {
	useFrameLoop,
	useReducedMotionSafe,
	useSceneClock,
} from '#app/utils/site/scene-clock'
import {
	LEASE_FINAL_PHASE,
	LEASE_KEYFRAMES,
	LEASE_LOOP_MS,
	LEASE_SCENE,
	LMR_COUNT,
	type LeasePill,
	formatLmrAmount,
	leaseStateAt,
	lmrProgressAt,
} from '#app/utils/site/scenes/lease-lmr'
import type { SceneProps } from './types'

/**
 * Landlord scene "Lease And LMR In Minutes" ("lease-lmr"): a lease card whose
 * clauses switch on, whose fields fill in from the approved application and
 * which both sides sign, beside a last month's rent card whose amount counts
 * up to $3,200 as the bar fills. Ten seconds, looping while in view.
 *
 * The timeline, the demo data and the phase-to-state mapping live in
 * app/utils/site/scenes/lease-lmr.ts; this file only draws a state. The
 * count-up is a motion value so the amount and the bar update without a
 * React render per frame.
 */
export function LeaseLmrScene({ active }: SceneProps) {
	const { phase } = useSceneClock({
		active,
		keyframes: LEASE_KEYFRAMES,
		loopMs: LEASE_LOOP_MS,
		finalPhase: LEASE_FINAL_PHASE,
	})
	const reduced = useReducedMotionSafe()
	const progress = useLmrProgress(active, reduced)
	const amount = useTransform(progress, (p) =>
		formatLmrAmount(LMR_COUNT.amount * p),
	)
	const fillX = useTransform(progress, (p) => `${(p - 1) * 100}%`)
	const state = leaseStateAt(phase)
	const copy = LEASE_SCENE

	return (
		<div
			data-scene="lease-lmr"
			data-phase={phase}
			aria-hidden="true"
			className="tt-scene-lease-lmr @container w-full max-w-[560px] shrink-0 leading-[1.3] md:-my-3.5 md:w-[calc(100%_+_40px)]"
		>
			<div className="grid grid-cols-[1.25fr_.75fr] items-center gap-3.5 max-[520px]:grid-cols-1 @max-[439px]:grid-cols-1">
				<article className="rounded-[22px] bg-white px-[18px] pb-4 pt-[18px] shadow-tt-card">
					{/* The pill is anchored top-right so a longer label ("Tenant
					    approved") can never wrap the address or change the card's
					    height mid-scene. */}
					<div className="relative">
						<Eyebrow>{copy.eyebrow}</Eyebrow>
						<div className="mt-1 whitespace-nowrap text-[18px] font-semibold tracking-[-0.015em] @max-[300px]:text-[16px]">
							{copy.address}
						</div>
						<StatusPill
							pill={state.status}
							className="absolute right-0 top-0"
						/>
					</div>

					<SectionLabel>{copy.clausesLabel}</SectionLabel>
					{copy.clauses.map((clause, index) => {
						const on = index < state.clausesOn
						return (
							<div key={clause} className={rowClass}>
								<span
									className={cn(
										'transition-[color,font-weight] duration-300',
										on ? 'font-medium text-tt-ink' : 'text-tt-muted',
									)}
								>
									{clause}
								</span>
								<span
									className={cn(
										'relative h-[17px] w-[30px] flex-none rounded-full transition-colors duration-300',
										on ? 'bg-tt-ink' : 'bg-tt-line',
									)}
								>
									<span
										className={cn(
											'absolute left-0.5 top-0.5 h-[13px] w-[13px] rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.2)] transition-transform ease-tt',
											on
												? 'translate-x-[13px] duration-[350ms]'
												: 'duration-200',
										)}
									/>
								</span>
							</div>
						)
					})}

					<SectionLabel>{copy.fieldsLabel}</SectionLabel>
					{copy.fields.map((field, index) => {
						const on = index < state.fieldsOn
						return (
							<div key={field.label} className={rowClass}>
								<span className="text-tt-muted">{field.label}</span>
								<b className="relative flex h-[18px] min-w-[100px] items-center justify-end font-semibold">
									<span
										className={cn(
											'absolute right-0 top-1 h-2.5 w-[100px] rounded-[5px] bg-tt-inset transition-opacity duration-300',
											on && 'opacity-0',
										)}
									/>
									<span
										className={cn(
											'relative whitespace-nowrap bg-white pl-1 transition-[opacity,transform] ease-tt',
											on
												? 'duration-[400ms]'
												: 'translate-y-1 opacity-0 duration-200',
										)}
									>
										{field.value}
									</span>
								</b>
							</div>
						)
					})}

					<div className="mt-3 flex gap-2">
						{copy.signatures.map((signature, index) => {
							const on = index === 0 ? state.tenantSigned : state.landlordSigned
							return (
								<div
									key={signature.label}
									className={cn(
										'min-w-0 flex-1 rounded-[10px] border px-2.5 py-[7px] text-[10.5px] text-tt-muted transition-[border-color,background-color] duration-300',
										on
											? 'border-solid border-[#CFE0B8] bg-[#F7FBF2]'
											: 'border-dashed border-tt-line',
									)}
								>
									{signature.label}
									<b
										data-on={on || undefined}
										className="tt-scene-lease-lmr__sig tt-script block h-[21px] whitespace-nowrap text-[17px] font-normal leading-[21px] text-tt-ink"
									>
										{signature.name}
									</b>
								</div>
							)
						})}
					</div>
				</article>

				<article className="rounded-[22px] bg-white p-[18px] shadow-tt-card">
					<Eyebrow>{copy.lmr.eyebrow}</Eyebrow>
					<motion.div className="tt-scene-lease-lmr__amount mt-2.5 text-[44px] leading-none text-tt-ink">
						{reduced ? formatLmrAmount(LMR_COUNT.amount) : amount}
					</motion.div>
					<div className="mb-3 mt-[14px] h-1.5 overflow-hidden rounded-[6px] bg-tt-inset">
						<motion.span
							className="block h-full w-full rounded-[6px] bg-tt-green"
							style={{ x: reduced ? '0%' : fillX }}
						/>
					</div>
					<StatusPill pill={state.lmr} pop />
				</article>
			</div>
		</div>
	)
}

const rowClass =
	'flex items-center justify-between gap-2.5 border-b border-tt-line-soft py-2 text-[13px]'

function Eyebrow({ children }: { children: string }) {
	return (
		<div className="text-[11px] font-medium uppercase tracking-[0.14em] text-tt-muted">
			{children}
		</div>
	)
}

function SectionLabel({ children }: { children: string }) {
	return (
		<div className="mb-1 mt-3.5 text-[10.5px] font-medium uppercase tracking-[0.12em] text-tt-muted">
			{children}
		</div>
	)
}

/**
 * A status pill. The label is keyed so a change remounts it and plays the
 * short fade-up in scene-lease-lmr.css; `pop` adds the reference's overshoot
 * when the pill turns green (the LMR card's "Received").
 */
function StatusPill({
	pill,
	pop = false,
	className,
}: {
	pill: LeasePill
	pop?: boolean
	className?: string
}) {
	return (
		<span
			className={cn(
				'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-[5px] text-[11px] font-semibold transition-colors duration-300',
				pill.green ? 'bg-tt-ok-bg text-tt-ok' : 'bg-tt-inset text-tt-body',
				pop && pill.green && 'tt-scene-lease-lmr__pop',
				className,
			)}
		>
			<span key={pill.label} className="tt-scene-lease-lmr__label">
				{pill.label}
			</span>
		</span>
	)
}

/**
 * The count-up as a motion value, 0..1, driven from the animation frame on
 * the same clock as the scene phases (both start the first frame after
 * `active` turns true and rewind when it turns false). No frames are taken
 * while the tile is out of view; the first frame of the next play writes
 * the rewound value. Reduced motion holds the end; the component then
 * renders the final strings directly so the server and client markup agree.
 */
function useLmrProgress(active: boolean, reduced: boolean) {
	const progress = useMotionValue(0)
	useFrameLoop(active && !reduced, (elapsed) => {
		const next = lmrProgressAt(elapsed)
		if (next !== progress.get()) progress.set(next)
	})
	return progress
}
